import { motion } from "framer-motion";
import cardImg from "../../assets/images/card.webp";
import cashImg from "../../assets/images/cash.webp";
import paySound from "../../../../assets/audio/pay.wav";
import { useAudioEffects } from "../../../../hooks/use-audio-effects";

interface PaymentButtonProps {
  type: "card" | "cash";
  className?: string;
  title?: string;
  onClick?: () => void;
}

// Single spring used across all children — ensures they feel physically linked
const SPRING = { type: "spring", stiffness: 480, damping: 22 } as const;
const SNAP = { type: "spring", stiffness: 700, damping: 20 } as const;

// All per-type values in one place
const config = {
  card: {
    color: "#8F72FE",
    border: "rgba(143,114,254,0.70)",
    glow: "rgba(143,114,254,0.48)",
    gradient: "linear-gradient(to top right, #8F72FE -100%, transparent 100%)",
    hoverGradient:
      "linear-gradient(to top right, #8F72FE -40%,  transparent 100%)",
  },
  cash: {
    color: "#ECAB2C",
    border: "rgba(236,171,44,0.70)",
    glow: "rgba(236,171,44,0.45)",
    gradient: "linear-gradient(to top right, #ECAB2C -40%, transparent 100%)",
    hoverGradient:
      "linear-gradient(to top right, #ECAB2C  10%, transparent 100%)",
  },
} as const;

// ── Child variants — keys must match parent's initial / whileHover / whileTap ──

const imageVariants = {
  rest: { y: 0, scale: 1, rotate: 0 },
  hover: { y: -8, scale: 1.12, rotate: -4 },
  tap: { y: -3, scale: 1.05, rotate: -2 },
};

const labelVariants = {
  rest: { x: 0, letterSpacing: "0em" },
  hover: { x: 5, letterSpacing: "0.02em" },
  tap: { x: 2, letterSpacing: "0em" },
};

const overlayVariants = {
  rest: { opacity: 0 },
  hover: { opacity: 1 },
  tap: { opacity: 0.7 },
};

// ─────────────────────────────────────────────────────────────────────────────

const PaymentButton = ({
  className = "",
  type,
  title,
  onClick = () => {},
}: PaymentButtonProps) => {
  const cfg = config[type];

  const { playEffect } = useAudioEffects();

  if (!title) title = `Pay via ${type}`;

  return (
    <motion.button
      onClick={() => {
        playEffect(paySound);

        onClick();
      }}
      type="button"
      title={title}
      className={`relative rounded-sm border p-3 overflow-hidden ${className}`}
      style={{
        background: cfg.gradient,
        borderColor: cfg.border,
        color: cfg.color,
      }}
      // ── Variant scope: all motion children inherit these state names ──
      initial="rest"
      whileHover="hover"
      whileTap="tap"
      variants={{
        rest: {
          scale: 1,
          y: 0,
          boxShadow: `0 0 0px ${cfg.glow}`,
        },
        hover: {
          scale: 1.04,
          y: -4,
          // Outer lift shadow + inner border bloom
          boxShadow: `0 8px 28px ${cfg.glow}, 0 0 0 1px ${cfg.border}`,
          transition: SPRING,
        },
        tap: {
          scale: 0.97,
          y: 1,
          boxShadow: `0 2px 10px ${cfg.glow}`,
          transition: SNAP,
        },
      }}
    >
      {/*
       * Gradient overlay: invisible at rest, fades in on hover to "boost"
       * the existing background gradient without snapping to a new value.
       */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        variants={overlayVariants}
        transition={{ duration: 0.22, ease: "easeOut" }}
        style={{ background: cfg.hoverGradient }}
      />

      {/*
       * Icon: inherits hover/tap variant from parent.
       * Floats up + tilts slightly — feels like it's being "picked up".
       */}
      <motion.img
        src={type === "card" ? cardImg : cashImg}
        alt=""
        aria-hidden
        draggable={false}
        className="absolute bottom-0 left-0"
        variants={imageVariants}
        transition={SPRING}
      />

      {/*
       * Label: slides right on hover — subtle but confirms interactivity.
       * z-10 keeps it above the gradient overlay.
       */}
      <motion.p
        className="relative z-10 ml-3 font-[500]"
        variants={labelVariants}
        transition={SPRING}
      >
        {title}
      </motion.p>
    </motion.button>
  );
};

export default PaymentButton;
